import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { Home, Plus, Pencil, Trash2, RefreshCw, Monitor, Copy, ExternalLink } from 'lucide-react';
import { useDisplays, useCreateDisplay, useUpdateDisplay, useRotateToken, useDeleteDisplay } from '../../api/displays';
import { useResources } from '../../api/referentiel';
import type { DisplayAdmin, DisplayCreated, DisplayInput } from '../../api/types';
import { ApiErrorMessage, Modal, NumberField, SelectField, TextField } from '../referentiel/ResourcesPanel';

/** Écran d'administration des téléviseurs d'atelier (lot 6). */
export default function DisplaysAdmin() {
  const { data: displays, isLoading, error } = useDisplays();
  const { data: resources } = useResources();
  const create = useCreateDisplay();
  const update = useUpdateDisplay();
  const rotate = useRotateToken();
  const remove = useDeleteDisplay();

  const [editing, setEditing] = useState<{ id?: number; data: DisplayInput } | null>(null);
  const [secret, setSecret] = useState<DisplayCreated | null>(null);

  function openNew() {
    setEditing({ data: { name: '', resourceIds: [], horizonDays: 2, pageSize: 4, dwellMs: 15000, theme: 'light' } });
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <h1 className="text-xl font-bold flex items-center gap-2"><Monitor size={20} /> Écrans d'atelier</h1>
          <Link to="/" className="flex items-center gap-2 text-blue-100 hover:text-white"><Home size={18} /> Accueil</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-500">Chaque écran a son périmètre de moyens, son rythme de rotation et son URL kiosque.</p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-2" onClick={openNew}>
            <Plus size={18} /> Nouvel écran
          </button>
        </div>

        {isLoading && <p className="text-gray-500">Chargement…</p>}
        {error && <p className="text-red-600">Impossible de charger les écrans.</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displays?.map((d) => (
            <DisplayCard
              key={d.id}
              display={d}
              onEdit={() => setEditing({ id: d.id, data: { name: d.name, resourceIds: d.resources.map((r) => r.id), horizonDays: d.horizonDays, pageSize: d.pageSize, dwellMs: d.dwellMs, theme: d.theme === 'dark' ? 'dark' : 'light' } })}
              onRotate={() => rotate.mutate(d.id, { onSuccess: (res) => setSecret(res) })}
              onDelete={() => { if (confirm(`Supprimer l'écran ${d.name} ? Son URL cessera de fonctionner.`)) remove.mutate(d.id); }}
            />
          ))}
          {displays && displays.length === 0 && (
            <div className="md:col-span-2 bg-white rounded-lg shadow p-8 text-center text-gray-500">Aucun écran. Créez-en un pour obtenir son QR code.</div>
          )}
        </div>
      </main>

      {editing && (
        <DisplayModal
          value={editing}
          resources={resources ?? []}
          saving={create.isPending || update.isPending}
          error={create.error ?? update.error}
          onClose={() => { create.reset(); update.reset(); setEditing(null); }}
          onSubmit={(data) => {
            if (editing.id) {
              update.mutate({ id: editing.id, data }, { onSuccess: () => { update.reset(); setEditing(null); } });
            } else {
              create.mutate(data, { onSuccess: (res) => { create.reset(); setEditing(null); setSecret(res); } });
            }
          }}
        />
      )}

      {secret && <SecretModal created={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

function statusOf(lastSeenAt: string | null): { label: string; cls: string } {
  if (!lastSeenAt) return { label: 'jamais vu', cls: 'bg-gray-300' };
  const ageMs = Date.now() - new Date(lastSeenAt).getTime();
  if (ageMs < 3 * 60_000) return { label: 'en ligne', cls: 'bg-green-500' };
  if (ageMs < 15 * 60_000) return { label: 'ralenti', cls: 'bg-amber-500' };
  return { label: 'hors ligne', cls: 'bg-red-500' };
}

function DisplayCard({ display, onEdit, onRotate, onDelete }: { display: DisplayAdmin; onEdit: () => void; onRotate: () => void; onDelete: () => void }) {
  const st = statusOf(display.lastSeenAt);
  return (
    <div className="bg-white rounded-lg shadow border border-gray-100">
      <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${st.cls}`} title={st.label} />
          <span className="font-semibold text-gray-800">{display.name}</span>
        </div>
        <div className="flex gap-2">
          <button className="text-blue-600 hover:text-blue-800" onClick={onEdit} aria-label="Modifier"><Pencil size={18} /></button>
          <button className="text-amber-600 hover:text-amber-800" onClick={onRotate} title="Régénérer le jeton (nouveau QR)"><RefreshCw size={18} /></button>
          <button className="text-red-600 hover:text-red-800" onClick={onDelete} aria-label="Supprimer"><Trash2 size={18} /></button>
        </div>
      </div>
      <dl className="p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div><dt className="text-gray-500">État</dt><dd className="text-gray-900">{st.label}{display.lastSeenAt ? ` · ${new Date(display.lastSeenAt).toLocaleTimeString('fr-FR')}` : ''}</dd></div>
        <div><dt className="text-gray-500">Rotation</dt><dd className="text-gray-900">{display.pageSize} cartes · {Math.round(display.dwellMs / 1000)} s</dd></div>
        <div className="col-span-2">
          <dt className="text-gray-500">Périmètre</dt>
          <dd className="text-gray-900">
            {display.resources.length === 0 ? 'Tous les moyens' : display.resources.map((r) => r.label).join(', ')}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function DisplayModal({ value, resources, saving, error, onClose, onSubmit }: {
  value: { id?: number; data: DisplayInput };
  resources: { id: number; label: string }[];
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (data: DisplayInput) => void;
}) {
  const [form, setForm] = useState<DisplayInput>(value.data);
  const set = <K extends keyof DisplayInput>(k: K, v: DisplayInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (id: number) => setForm((f) => ({ ...f, resourceIds: f.resourceIds.includes(id) ? f.resourceIds.filter((x) => x !== id) : [...f.resourceIds, id] }));

  return (
    <Modal title={value.id ? "Modifier l'écran" : 'Nouvel écran'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4">
        <TextField label="Nom" value={form.name} onChange={(v) => set('name', v)} required />

        <div className="grid grid-cols-3 gap-4">
          <NumberField label="Horizon (jours)" value={form.horizonDays} onChange={(v) => set('horizonDays', v)} min={1} max={14} />
          <NumberField label="Cartes / page" value={form.pageSize} onChange={(v) => set('pageSize', v)} min={2} max={6} />
          <NumberField label="Durée page (s)" value={Math.round(form.dwellMs / 1000)} onChange={(v) => set('dwellMs', v * 1000)} min={3} max={120} />
        </div>

        <SelectField label="Thème d'affichage" value={form.theme} onChange={(v) => set('theme', v as 'light' | 'dark')} options={[['light', 'Clair'], ['dark', 'Sombre']]} />

        <fieldset>
          <legend className="text-sm text-gray-600 mb-1">Périmètre (moyens affichés)</legend>
          <p className="text-xs text-gray-400 mb-2">Aucun coché = tous les moyens.</p>
          <div className="flex flex-wrap gap-2">
            {resources.map((r) => {
              const on = form.resourceIds.includes(r.id);
              return (
                <button type="button" key={r.id} onClick={() => toggle(r.id)}
                  className={`px-3 py-1.5 rounded border text-sm ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <ApiErrorMessage error={error} />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>Annuler</button>
          <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={saving || !form.name}>
            {saving ? 'Enregistrement…' : value.id ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Affiche le QR code + l'URL kiosque, montrés une seule fois après création/rotation. */
function SecretModal({ created, onClose }: { created: DisplayCreated; onClose: () => void }) {
  const kioskUrl = `${window.location.origin}${created.kioskPath}`;
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    QRCode.toDataURL(kioskUrl, { width: 240, margin: 1 }).then(setQr).catch(() => setQr(null));
  }, [kioskUrl]);

  return (
    <Modal title={`Écran « ${created.name} »`} onClose={onClose}>
      <div className="space-y-4 text-center">
        <p className="text-sm text-gray-600">Scannez ce QR code depuis le navigateur du téléviseur, ou ouvrez l'URL ci-dessous. Le jeton n'est affiché qu'ici.</p>
        {qr ? <img src={qr} alt="QR code kiosque" className="mx-auto border rounded" /> : <p className="text-gray-400">Génération du QR…</p>}
        <div className="flex items-center gap-2 bg-gray-50 border rounded px-2 py-1.5">
          <code className="text-xs text-gray-700 truncate flex-grow text-left">{kioskUrl}</code>
          <button className="text-blue-600 hover:text-blue-800 shrink-0" title="Copier"
            onClick={() => { navigator.clipboard.writeText(kioskUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>
            <Copy size={16} />
          </button>
          <a className="text-blue-600 hover:text-blue-800 shrink-0" href={kioskUrl} target="_blank" rel="noreferrer" title="Ouvrir"><ExternalLink size={16} /></a>
        </div>
        {copied && <p className="text-xs text-green-600">Copié.</p>}
        <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={onClose}>J'ai noté</button>
      </div>
    </Modal>
  );
}
